
import { CheckCircle2, XCircle, Lightbulb } from "lucide-react";
import { HighlightedText } from "./HighlightedText";
import { Separator } from "@/components/ui/separator";
import type { Evaluation } from "@/types/evaluation";

interface EvaluationResultsProps {
  evaluation: Evaluation;
  essayContent: string;
}

export const EvaluationResults = ({ evaluation, essayContent }: EvaluationResultsProps) => {
  const highlights = [
    ...(evaluation.feedback?.strengths?.map(strength => ({
      text: strength.quote,
      className: "bg-green-100 hover:bg-green-200 dark:bg-green-900/30 dark:hover:bg-green-800/40",
      hoverContent: (
        <div className="space-y-2 dark:bg-stone-800 dark:border-stone-700">
          <p className="font-medium text-green-700 dark:text-green-400">{strength.skill_demonstrated}</p>
          <p className="dark:text-white">{strength.explanation}</p>
        </div>
      )
    })) || []),
    ...(evaluation.feedback?.weaknesses?.map(weakness => ({
      text: weakness.quote,
      className: "bg-red-100 hover:bg-red-200 dark:bg-red-900/30 dark:hover:bg-red-800/40",
      hoverContent: (
        <div className="space-y-2 dark:bg-stone-800 dark:border-stone-700">
          <p className="font-medium text-red-700 dark:text-red-400">{weakness.issue}</p>
          <p className="text-green-700 dark:text-green-400">Suggested improvement: {weakness.improvement}</p>
        </div>
      )
    })) || []),
    ...(evaluation.highlighted_phrases?.effective_vocabulary?.filter(Boolean)?.map(text => ({
      text,
      className: "bg-blue-100 hover:bg-blue-200 dark:bg-blue-900/30 dark:hover:bg-blue-800/40",
      hoverContent: <p className="dark:text-white dark:bg-stone-800 dark:border-stone-700">Effective vocabulary usage</p>
    })) || []),
    ...(evaluation.highlighted_phrases?.good_structures?.filter(Boolean)?.map(text => ({
      text,
      className: "bg-purple-100 hover:bg-purple-200 dark:bg-purple-900/30 dark:hover:bg-purple-800/40",
      hoverContent: <p className="dark:text-white dark:bg-stone-800 dark:border-stone-700">Good grammatical structure</p>
    })) || []),
    ...(evaluation.highlighted_phrases?.areas_to_improve?.filter(Boolean)?.map(text => ({
      text,
      className: "bg-yellow-100 hover:bg-yellow-200 dark:bg-yellow-900/30 dark:hover:bg-yellow-800/40",
      hoverContent: <p className="dark:text-white dark:bg-stone-800 dark:border-stone-700">Area for potential improvement</p>
    })) || [])
  ];

  return (
    <div className="space-y-6 feedback-container">
      <HighlightedText content={essayContent} highlights={highlights} />

      <Separator className="dark:border-stone-600" />

      <div className="grid gap-4 grid-cols-1 md:grid-cols-5">
        <div className="text-center col-span-1 md:col-span-1 bg-white dark:bg-stone-800 p-4 rounded-lg border border-stone-100 dark:border-stone-700">
          <div className="text-xl font-semibold dark:text-white">{evaluation.overall_score}</div>
          <div className="text-sm text-stone-500 dark:text-stone-500">Overall</div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 col-span-1 md:col-span-4 gap-4">
          <div className="text-center bg-white dark:bg-stone-800 p-4 rounded-lg border border-stone-100 dark:border-stone-700">
            <div className="text-xl font-semibold dark:text-white">{evaluation.task_achievement_score}</div>
            <div className="text-sm text-stone-500 dark:text-stone-500">Task Achievement</div>
          </div>
          <div className="text-center bg-white dark:bg-stone-800 p-4 rounded-lg border border-stone-100 dark:border-stone-700">
            <div className="text-xl font-semibold dark:text-white">{evaluation.coherence_cohesion_score}</div>
            <div className="text-sm text-stone-500 dark:text-stone-500">Coherence</div>
          </div>
          <div className="text-center bg-white dark:bg-stone-800 p-4 rounded-lg border border-stone-100 dark:border-stone-700">
            <div className="text-xl font-semibold dark:text-white">{evaluation.lexical_resource_score}</div>
            <div className="text-sm text-stone-500 dark:text-stone-500">Lexical</div>
          </div>
          <div className="text-center bg-white dark:bg-stone-800 p-4 rounded-lg border border-stone-100 dark:border-stone-700">
            <div className="text-xl font-semibold dark:text-white">{evaluation.grammatical_range_score}</div>
            <div className="text-sm text-stone-500 dark:text-stone-500">Grammar</div>
          </div>
        </div>
      </div>

      <Separator className="dark:border-stone-600" />

      <div className="space-y-8">
        <div>
          <h4 className="flex items-center gap-2 font-semibold mb-4 dark:text-white">
            <CheckCircle2 className="h-5 w-5 text-green-500" />
            Strengths
          </h4>
          <ul className="space-y-8">
            {evaluation.feedback?.strengths?.map((strength, index) => (
              <li key={index} className="space-y-4">
                <div className="flex flex-col md:flex-row gap-4 md:items-center">
                  <p className="text-sm leading-relaxed stone-900 dark:text-white">{strength.explanation}</p>
                  <p className="text-xs text-stone-500 dark:text-stone-500 whitespace-nowrap">
                    Skill: <span className="font-semibold">{strength.skill_demonstrated}</span>
                  </p>
                </div>
                <blockquote className="border-l-2 border-green-500 pl-4 italic text-sm text-stone-500 dark:text-stone-500">
                  "{strength.quote}"
                </blockquote>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h4 className="flex items-center gap-2 font-semibold mb-4 dark:text-white">
            <XCircle className="h-5 w-5 text-red-500" />
            Areas for Improvement
          </h4>
          <ul className="space-y-8">
            {evaluation.feedback?.weaknesses?.map((weakness, index) => (
              <li key={index} className="space-y-4">
                <div className="flex flex-col space-y-4">
                  <p className="text-sm leading-relaxed stone-900 dark:text-white">{weakness.issue}</p>
                  <p className="text-sm leading-relaxed text-green-700 dark:text-green-400">
                    Suggested rewrite: {weakness.improvement}
                  </p>
                </div>
                <blockquote className="border-l-2 border-red-500 pl-4 italic text-sm text-stone-500 dark:text-stone-500">
                  "{weakness.quote}"
                </blockquote>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h4 className="flex items-center gap-2 font-semibold mb-4 dark:text-white">
            <Lightbulb className="h-5 w-5 text-yellow-500" />
            Suggestions
          </h4>
          <ul className="space-y-8">
            {evaluation.feedback?.suggested_improvements?.map((improvement, index) => (
              <li key={index} className="space-y-4">
                <div className="flex flex-col space-y-4">
                  <h5 className="text-sm font-medium capitalize text-stone-900 dark:text-white">{improvement.area}</h5>
                  <p className="text-sm leading-relaxed text-stone-900 dark:text-white">{improvement.suggestion}</p>
                </div>
                <blockquote className="border-l-2 border-yellow-500 pl-4 italic text-sm text-stone-500 dark:text-stone-500">
                  Example: "{improvement.example}"
                </blockquote>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};
